import type { User } from "oidc-client-ts";
import { RateLimiter } from "limiter";
import TooManyRequestsException from "@rmp/core/exceptions/tooManyRequestsException";
import SecurityService from "@rmp/core/services/auth/securityService";
import type { AxiosInstance } from "axios";
import i18n from "@rmp/core/plugins/i18n/core";

const rateLimiter = new RateLimiter({ tokensPerInterval: 15, interval: "minute" });

const addAuthInterceptor = (instance: AxiosInstance) => {
	const securityService = new SecurityService();

	instance.interceptors.request.use(async config => {
		try {
			const token = await securityService.getToken();
			
			if(token) {
				config.headers.Authorization = `Bearer ${token}`;
			}

			return config;
		} catch (e) {
			console.error(e);
			throw e;
		}
	}, (error) => {
		return Promise.reject(error);
	});
	
	
	instance.interceptors.response.use((response) => {
		return response;
	}, async (error) => {
		// Отличные от 401 не обрабатываются
		if(!error.response || error.response.status !== 401)
			return Promise.reject(error);
		
		// Запросы с этим флагом не должны обновлять токен и разлогинивать пользователя при 401
		// (например, ошибка валидации ЭП при выборе профиля - пользователь уже аутентифицирован)
		if(error.config?.skipAuthRefresh)
			return Promise.reject(error);
		
		let user: User | null;
		
		try {
			user = await securityService.signinSilent();
		} catch {
			// Сессия сбрасывается, если возникла ошибка при обновлении токена
			await securityService.signOut();
			return Promise.reject(error);
		}
		
		if(!user) {
			await securityService.signOut();
			return Promise.reject(error);
		}
		
		if(!rateLimiter.tryRemoveTokens(1)) {
			return Promise.reject(new TooManyRequestsException(i18n.t("errors.tooManyRequests").toString()));
		}

		// Повторный запрос с обновленным токеном
		const config = {
			...error.config,
			// Axios уже преобразовал тело перед первым запросом: повторная сериализация изменит JSON.
			transformRequest: [(data: unknown) => data],
		};
		config.headers.Authorization = `Bearer ${user.access_token}`;
		return instance.request(config);
	});
};

export default addAuthInterceptor;
